import { useState } from "react";
import {
  Box,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  Button,
  Stack,
  Divider,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridPagination,
} from "@mui/x-data-grid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  useMatchingRuns,
  useMatchingRunDetail,
  useMatchingRunSummary,
  useCorpusSnapshot,
  useRetryErrors,
  useConfirmPair,
  useRejectPair,
  usePairStatsByCategory,
  MatchingRunItem,
  MatchDecisionItem,
} from "../api/usePairMapper";
import {
  CATEGORY_LABELS,
  PredictionCategory,
} from "../../Predictions/constants/categories";

const formatDate = (d: string) => {
  const date = new Date(d);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (ms: number) => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const formatNum = (n: number) => n.toLocaleString("en-US");

const statusColor = (s: string) => {
  if (s === "completed") return "success";
  if (s === "running") return "info";
  return "error";
};

const triggerColor = (t: string) => {
  if (t === "manual") return "primary";
  if (t === "scheduled") return "default";
  if (t === "rematch") return "secondary";
  return "warning";
};

const outcomeColor = (o: string) => {
  if (o === "matched") return "success";
  if (o === "error") return "error";
  if (o === "no_candidates") return "default";
  return "warning";
};

// ─── Stat tile (shared by the aggregate card + run detail panel) ──
function StatTile({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}) {
  return (
    <Box sx={{ minWidth: 92 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", textTransform: "uppercase", fontSize: "0.62rem" }}
      >
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{ fontSize: "1.1rem", fontWeight: 700, color: color ?? "common.white", lineHeight: 1.2 }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

// ─── Corpus Snapshot Card ─────────────────────────────────────────
// Standing DB-state snapshot — independent of any time window. Sits above
// the Matcher activity card so the operator sees "what's in the corpus
// right now" before drilling into recent runs.
function CorpusSnapshotCard() {
  const { data, isLoading } = useCorpusSnapshot();

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        bgcolor: "rgba(255,255,255,0.03)",
        borderRadius: 1,
        border: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      <Typography variant="subtitle2" sx={{ color: "common.white", mb: 1.5 }}>
        Corpus
      </Typography>
      {isLoading || !data ? (
        <CircularProgress size={20} />
      ) : (
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <StatTile
            label="Cached markets"
            value={formatNum(data.cachedMarkets.total)}
            sub={`poly ${formatNum(data.cachedMarkets.polymarket)} · kalshi ${formatNum(data.cachedMarkets.kalshi)}`}
          />
          <StatTile
            label="Active pairs"
            value={formatNum(data.activePairs.total)}
            color={data.activePairs.total > 0 ? "success.main" : undefined}
            sub={
              `det ${formatNum(data.activePairs.byMatchedBy.deterministic)}` +
              ` · sem_auto ${formatNum(data.activePairs.byMatchedBy.semantic_auto)}` +
              ` · sem_fb ${formatNum(data.activePairs.byMatchedBy.semantic_fallback)}` +
              ` · manual ${formatNum(data.activePairs.byMatchedBy.manual)}`
            }
          />
        </Stack>
      )}
    </Box>
  );
}

// ─── Aggregate Summary Card ───────────────────────────────────────
const WINDOW_OPTIONS = [
  { value: 24, label: "24h" },
  { value: 168, label: "7d" },
];

function RunSummaryCard() {
  const [sinceHours, setSinceHours] = useState(24);
  const { data, isLoading } = useMatchingRunSummary(sinceHours);

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        bgcolor: "rgba(255,255,255,0.03)",
        borderRadius: 1,
        border: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: "common.white" }}>
          Matcher activity
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={sinceHours}
          onChange={(_, v) => v && setSinceHours(v)}
        >
          {WINDOW_OPTIONS.map((o) => (
            <ToggleButton key={o.value} value={o.value} sx={{ fontSize: "0.7rem", px: 1.5, py: 0.25 }}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      {isLoading || !data ? (
        <CircularProgress size={20} />
      ) : (
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <StatTile
            label="Runs"
            value={formatNum(data.runCount)}
            sub={`${data.completed} ok · ${data.failed} failed`}
          />
          <StatTile
            label="Markets fetched"
            value={formatNum(data.totalMarketsFetched)}
            sub={`poly ${formatNum(data.totalMarketsFetchedPolymarket)} · kalshi ${formatNum(data.totalMarketsFetchedKalshi)}`}
          />
          <StatTile
            label="New markets"
            value={formatNum(data.totalNewMarketsFetched)}
            sub={`poly ${formatNum(data.totalNewMarketsFetchedPolymarket)} · kalshi ${formatNum(data.totalNewMarketsFetchedKalshi)}`}
          />
          <StatTile
            label="Pairs created"
            value={formatNum(data.totalPairsCreated)}
            color={data.totalPairsCreated > 0 ? "success.main" : undefined}
          />
          <StatTile
            label="Errors"
            value={formatNum(data.totalErrors)}
            color={data.totalErrors > 0 ? "error.main" : undefined}
          />
        </Stack>
      )}
    </Box>
  );
}

// ─── Per-category breakdown for one run ───────────────────────────
function RunCategoryBreakdown({ runId }: { runId: string }) {
  const { data, isLoading } = usePairStatsByCategory(runId);

  if (isLoading) {
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Loading category breakdown…
      </Typography>
    );
  }

  const entries = Object.entries(data?.byCategory ?? {}).sort(
    (a, b) => b[1].total - a[1].total,
  );

  if (entries.length === 0) {
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        No pairs from this run remain in the collection.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
      {entries.map(([cat, info]) => {
        const subParts = Object.entries(info.bySubcategory)
          .sort((a, b) => b[1] - a[1])
          .map(([sub, n]) => `${sub}: ${n}`);
        const label =
          CATEGORY_LABELS[cat as PredictionCategory] ?? cat;
        return (
          <Tooltip
            key={cat}
            title={subParts.length ? subParts.join("  ·  ") : ""}
          >
            <Chip
              label={`${label} ${info.total}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem" }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}

// ─── Run Detail Panel ─────────────────────────────────────────────
function RunDetailPanel({
  runId,
  onClose,
  onViewRunPairs,
}: {
  runId: string;
  onClose: () => void;
  onViewRunPairs?: (runId: string, category: PredictionCategory) => void;
}) {
  const { data: run, isLoading } = useMatchingRunDetail(runId);
  const { data: categoryStats } = usePairStatsByCategory(runId);
  const [filter, setFilter] = useState<string>("all");
  const confirmPair = useConfirmPair();
  const rejectPair = useRejectPair();
  const retryErrors = useRetryErrors();

  if (isLoading || !run) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  const decisions = run.decisions || [];
  const matched = decisions.filter((d) => d.outcome === "matched");
  const noMatch = decisions.filter(
    (d) => d.outcome === "no_match" || d.outcome === "no_candidates",
  );
  const errored = decisions.filter((d) => d.outcome === "error");

  const filtered =
    filter === "matched"
      ? matched
      : filter === "unmatched"
        ? noMatch
        : filter === "errors"
          ? errored
          : decisions;

  const fetched = run.marketsFetched;
  const fetchedLabel = fetched == null ? "—" : formatNum(fetched);
  const fetchedSub =
    run.newMarketsFetched != null ? `${formatNum(run.newMarketsFetched)} new` : undefined;

  // Largest category from the run becomes the landing tab when jumping to the
  // scoped browse view. `byCategory` keys include `uncategorized`, which is a
  // valid mapper tab value.
  const pairTotal = categoryStats?.total ?? 0;
  const topCategory = Object.entries(categoryStats?.byCategory ?? {}).sort(
    (a, b) => b[1].total - a[1].total,
  )[0]?.[0] as PredictionCategory | undefined;

  return (
    <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.02)", borderRadius: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: 'common.white' }}>
          Run {formatDate(run.startedAt)}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {errored.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<ReplayIcon />}
              onClick={() => retryErrors.mutate(runId)}
              disabled={retryErrors.isLoading}
            >
              Retry Errors
            </Button>
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* ── Run summary stats ── */}
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <StatTile label="Fetched from API" value={fetchedLabel} sub={fetchedSub} />
        <StatTile
          label="Ready to match"
          value={formatNum(
            (run.newMarketsPolymarket || 0) + (run.newMarketsKalshi || 0),
          )}
          sub={`poly ${run.newMarketsPolymarket || 0} · kalshi ${run.newMarketsKalshi || 0}`}
        />
        <StatTile
          label="Pairs created"
          value={formatNum(run.matchesCreated || 0)}
          color={run.matchesCreated > 0 ? "success.main" : undefined}
        />
        <StatTile label="Duration" value={formatDuration(run.durationMs || 0)} />
        <StatTile
          label="Errors"
          value={formatNum(run.errors?.length || 0)}
          color={run.errors?.length ? "error.main" : undefined}
        />
      </Stack>

      {/* ── Per-category breakdown + view pairs ── */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.75,
          }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Pairs by category ({pairTotal})
          </Typography>
          {onViewRunPairs && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              disabled={pairTotal === 0 || !topCategory}
              onClick={() =>
                topCategory && onViewRunPairs(runId, topCategory)
              }
              sx={{ fontSize: "0.7rem" }}
            >
              View {pairTotal} pairs
            </Button>
          )}
        </Box>
        <RunCategoryBreakdown runId={runId} />
      </Box>

      <Divider sx={{ mb: 1.5, borderColor: "rgba(255,255,255,0.07)" }} />

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
        <Chip
          label={`${matched.length} matched`}
          size="small"
          color="success"
          variant="outlined"
          sx={{ fontSize: "0.7rem" }}
        />
        <Chip
          label={`${noMatch.length} unmatched`}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ fontSize: "0.7rem" }}
        />
        {errored.length > 0 && (
          <Chip
            label={`${errored.length} errors`}
            size="small"
            color="error"
            variant="outlined"
            sx={{ fontSize: "0.7rem" }}
          />
        )}
      </Box>

      <ToggleButtonGroup
        size="small"
        value={filter}
        exclusive
        onChange={(_, v) => v && setFilter(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="all" sx={{ fontSize: "0.7rem", px: 1.5 }}>
          All ({decisions.length})
        </ToggleButton>
        <ToggleButton value="matched" sx={{ fontSize: "0.7rem", px: 1.5 }}>
          Matched ({matched.length})
        </ToggleButton>
        <ToggleButton value="unmatched" sx={{ fontSize: "0.7rem", px: 1.5 }}>
          Unmatched ({noMatch.length})
        </ToggleButton>
        <ToggleButton value="errors" sx={{ fontSize: "0.7rem", px: 1.5 }}>
          Errors ({errored.length})
        </ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ maxHeight: 400, overflow: "auto" }}>
        {filtered.map((d, i) => (
          <DecisionRow
            key={`${d.sourceExternalId}-${i}`}
            decision={d}
            onConfirm={
              d.pairId ? () => confirmPair.mutate(d.pairId!) : undefined
            }
            onReject={
              d.pairId ? () => rejectPair.mutate(d.pairId!) : undefined
            }
          />
        ))}
        {filtered.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 2, textAlign: "center" }}
          >
            No decisions in this category
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Single Decision Row ──────────────────────────────────────────
function DecisionRow({
  decision: d,
  onConfirm,
  onReject,
}: {
  decision: MatchDecisionItem;
  onConfirm?: () => void;
  onReject?: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        py: 0.75,
        px: 1,
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        "&:hover": { bgcolor: "rgba(255,255,255,0.03)" },
      }}
    >
      <Chip
        label={d.outcome.replace("_", " ")}
        size="small"
        color={outcomeColor(d.outcome) as any}
        sx={{ fontSize: "0.6rem", minWidth: 80 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontSize: "0.75rem", color: "common.white" }}
        >
          <strong>{d.sourcePlatform === "polymarket" ? "Poly" : "Kalshi"}:</strong>{" "}
          {d.sourceTitle}
        </Typography>
        {d.outcome === "matched" && d.matchedTitle && (
          <Typography
            variant="body2"
            noWrap
            sx={{ fontSize: "0.75rem", color: "text.secondary" }}
          >
            <strong>
              {d.matchedPlatform === "polymarket" ? "Poly" : "Kalshi"}:
            </strong>{" "}
            {d.matchedTitle}
          </Typography>
        )}
        {d.reasoning && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontStyle: "italic" }}
          >
            {d.reasoning}
          </Typography>
        )}
        {d.outcome === "error" && d.errorMessage && (
          <Typography variant="caption" sx={{ color: "error.main" }}>
            {d.errorMessage}
          </Typography>
        )}
      </Box>
      {d.confidence != null && (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color:
              d.confidence >= 0.8
                ? "success.main"
                : d.confidence >= 0.5
                  ? "warning.main"
                  : "error.main",
          }}
        >
          {Math.round(d.confidence * 100)}%
        </Typography>
      )}
      {d.outcome === "matched" && onConfirm && onReject && (
        <Box sx={{ display: "flex", gap: 0.25 }}>
          <Tooltip title="Confirm match">
            <IconButton size="small" color="success" onClick={onConfirm}>
              <CheckIcon sx={{ fontSize: "0.9rem" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject match">
            <IconButton size="small" color="error" onClick={onReject}>
              <CloseIcon sx={{ fontSize: "0.9rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

// ─── Main Tab Component ───────────────────────────────────────────
export function MatchingHistoryTab({
  onViewRunPairs,
}: {
  onViewRunPairs?: (runId: string, category: PredictionCategory) => void;
}) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  const { data, isLoading } = useMatchingRuns({
    limit: pageSize,
    offset: page * pageSize,
  });

  const columns: GridColDef<MatchingRunItem>[] = [
    {
      headerName: "",
      field: "expand",
      width: 40,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <IconButton
          size="small"
          onClick={() =>
            setExpandedRunId(expandedRunId === row._id ? null : row._id)
          }
        >
          {expandedRunId === row._id ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      ),
    },
    {
      headerName: "Date",
      field: "startedAt",
      width: 140,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
          {formatDate(value)}
        </Typography>
      ),
    },
    {
      headerName: "Platform",
      field: "platform",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) =>
        value ? (
          <Chip
            label={value === "polymarket" ? "poly" : value}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.65rem" }}
          />
        ) : (
          <Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            —
          </Typography>
        ),
    },
    {
      headerName: "Trigger",
      field: "trigger",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Chip
          label={value}
          size="small"
          color={triggerColor(value) as any}
          sx={{ fontSize: "0.65rem" }}
        />
      ),
    },
    {
      headerName: "Status",
      field: "status",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Chip
          label={value}
          size="small"
          color={statusColor(value) as any}
          sx={{ fontSize: "0.65rem" }}
        />
      ),
    },
    {
      headerName: "Duration",
      field: "durationMs",
      width: 80,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
          {formatDuration(value)}
        </Typography>
      ),
    },
    {
      headerName: "Fetched",
      field: "marketsFetched",
      width: 90,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
          {value == null ? "—" : formatNum(value)}
        </Typography>
      ),
    },
    {
      headerName: "New",
      field: "newMarketsFetched",
      width: 75,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
          {value == null ? "—" : formatNum(value)}
        </Typography>
      ),
    },
    {
      headerName: "Pairs",
      field: "matchesCreated",
      width: 75,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.8rem",
            fontWeight: 600,
            color: value > 0 ? "success.main" : "text.secondary",
          }}
        >
          {value}
        </Typography>
      ),
    },
    {
      headerName: "Errors",
      field: "errors",
      width: 70,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.8rem",
            fontWeight: value?.length > 0 ? 600 : 400,
            color: value?.length > 0 ? "error.main" : "text.secondary",
          }}
        >
          {value?.length || 0}
        </Typography>
      ),
    },
  ];

  const rows = data?.runs || [];

  return (
    <Box sx={{ width: "100%" }}>
      <CorpusSnapshotCard />
      <RunSummaryCard />
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row._id}
        loading={isLoading}
        autoHeight
        rowHeight={42}
        disableSelectionOnClick
        rowCount={data?.total || 0}
        paginationMode="server"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        rowsPerPageOptions={[10, 25, 50]}
        components={{ Pagination: GridPagination }}
        sx={{
          border: "none",
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(255,255,255,0.05)",
          },
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          },
        }}
      />
      {expandedRunId && (
        <Collapse in={!!expandedRunId} timeout={200}>
          <RunDetailPanel
            runId={expandedRunId}
            onClose={() => setExpandedRunId(null)}
            onViewRunPairs={onViewRunPairs}
          />
        </Collapse>
      )}
    </Box>
  );
}
